import { useQuery } from '@tanstack/react-query';
import { toZonedTime } from 'date-fns-tz';

import { buildSnapshot, parseSahkotinResponse, type ElectricitySnapshot } from './electricityDerive';

export type { ElectricitySnapshot } from './electricityDerive';

const TZ = 'Europe/Helsinki';

/** Get start of the current Helsinki day as an ISO string (UTC). */
function helsinkiDayBoundary(nowMs: number, offsetDays: number): string {
  const zoned = toZonedTime(nowMs, TZ);
  const y = zoned.getFullYear();
  const m = zoned.getMonth();
  const d = zoned.getDate() + offsetDays;
  const midnightLocal = new Date(y, m, d, 0, 0, 0, 0);
  const helsinkiAtMidnight = toZonedTime(midnightLocal.getTime(), TZ);
  const diff = midnightLocal.getTime() - helsinkiAtMidnight.getTime();
  return new Date(midnightLocal.getTime() + diff).toISOString();
}

/** Helsinki YYYY-MM-DD for query key rotation at midnight. */
function helsinkiDayKey(nowMs: number): string {
  const zoned = toZonedTime(nowMs, TZ);
  const y = zoned.getFullYear();
  const m = String(zoned.getMonth() + 1).padStart(2, '0');
  const d = String(zoned.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

async function fetchPrices(nowMs: number): Promise<ElectricitySnapshot> {
  const start = helsinkiDayBoundary(nowMs, 0);
  const end = helsinkiDayBoundary(nowMs, 2); // end-of-tomorrow

  const url = `https://sahkotin.fi/prices?quarter&fix&vat&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Sahkotin API error: ${res.status}`);

  const json: unknown = await res.json();
  const slots = parseSahkotinResponse(json);
  return buildSnapshot(slots, nowMs);
}

export default function useElectricity() {
  const nowMs = Date.now();
  const dayKey = helsinkiDayKey(nowMs);

  return useQuery({
    queryKey: ['electricity', 'sahkotin', dayKey],
    queryFn: () => fetchPrices(Date.now()),
    staleTime: 15 * 60_000,
    refetchInterval: 15 * 60_000,
    gcTime: 60 * 60_000,
  });
}
